/**
 * Unified Search Bar Input Component for FlowRide Frontend
 * 
 * Handles both departure and arrival location input with autocomplete functionality,
 * search history, and integration with the itinerary service. This unified component
 * replaces the previous separate ArrivalInput and DepartureInput components.
 * 
 * @module SearchBarInput
 * @example
 * ```tsx
 * <SearchBarInput
 *   isArrival={false}
 *   autoComplete={autocompleteValues}
 *   input={departureInput}
 *   setInput={setDepartureInput}
 *   // ... other props
 * />
 * ```
 */

import { IonSpinner } from "@ionic/react";
import { AnimatePresence } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { SEARCH_BAR } from "../../config/global.configuration";
import { usePageVisibility } from "../../hooks/usePageVisibility.hook";
import { useSearchBarAutocomplete } from "../../hooks/useSearchBarAutocomplete";
import ArrivalIcon from "../../image/icons/Inputs/ArrivalIcon.svg";
import DarkCloseIcon from "../../image/icons/Inputs/DarkCloseIcon.svg";
import DepartureIcon from "../../image/icons/Inputs/DepartureIcon.svg";
import { ItineraryService } from "../../services/ItineraryService";
import { itineraryPanelStateSlice, useEphemeralSelector, useMapInstance } from "../../store/ephemeral";
import { appendSearchHistoryItem, searchHistorySlice, usePersistedSlice } from "../../store/persistence";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { type ItineraryDTO } from "../../types/dtos/api/itinerary/itinerary.dto";
import { type DateWithMode, type Route } from "../../types/Forms";
import { GeocodeSource, GeocodeType, SearchResultItem } from "../../types/geocode-ui.types";
import { type ItineraryListContentMode } from "../../types/ItineraryPlanner";
import AutocompleteList from "./AutocompleteList";
import CalendarSection from "./CalendarSection";
import DepartureDisplay from "./DepartureDisplay";

/**
 * Base props common to both departure and arrival input types
 * 
 * @interface BaseSearchBarInputProps
 */
interface BaseSearchBarInputProps {

    /** Selected departure address */
    "selectedAddress": SearchResultItem | null;

    /** Setter for selected departure address */
    "setSelectedAddress": Dispatch<SetStateAction<SearchResultItem | null>>;

    /** Whether this is an arrival input (true) or departure input (false) */
    "isArrival": boolean;

    /** Autocomplete values array */
    "autoComplete": SearchResultItem[];

    /** Setter for autocomplete values */
    "setAutocompleteValues": Dispatch<SetStateAction<SearchResultItem[]>>;

    /** Current latitude for location-based autocomplete */
    "lat": number;

    /**
     * Current longitude for location-based autocomplete
     */
    "lon": number;

    /** Input value string */
    "input": string;

    /** Setter for input value */
    "setInput": Dispatch<SetStateAction<string>>;

    /** Whether input is in merge mode (both departure and arrival are set) */
    "isMerge": boolean;

    /** Setter for merge mode */
    "setIsMerge": Dispatch<SetStateAction<boolean>>;

    /** Reference to the input element */
    "inputRef": React.RefObject<HTMLInputElement>;

    /** Setter for selected itinerary */
    "setSelectedItinerary": Dispatch<SetStateAction<ItineraryDTO | null>>;

    /** Setter for itinerary panel height reset */
    "onResetPanelExpanded": () => void;

    /** Setter for car itinerary data */
    "setITakeMyCarData": Dispatch<SetStateAction<ItineraryDTO | null>>;

    /** Callback function for swap action */
    "onSwap"?: () => void;

    /** My position autocomplete item */
    "myPositionAutocomplete": SearchResultItem;

    /** Whether dark palette is active for overlays */
    "isDarkUi": boolean;
}

/**
 * Props specific to departure input type
 * 
 * @interface DepartureSpecificProps
 */
interface DepartureSpecificProps {

    /** Arrival input value (used to check if merge mode should be enabled) */
    "arrivalInput": string;

    /** Arrival selected address (for swap functionality) */
    "arrivalSelectedAddress"?: SearchResultItem | null;
}

/**
 * Props specific to arrival input type
 * 
 * @interface ArrivalSpecificProps
 */
interface ArrivalSpecificProps {

    /** Visibility state of the component */
    "visible": boolean;

    /** Setter for visibility state */
    "setVisible": Dispatch<SetStateAction<boolean>>;

    /** Position for styling (CSS top value in pixels) */
    "position": string;

    /** Optional legacy setter (arrival top is driven from merge state in Home). */
    "setPosition"?: Dispatch<SetStateAction<string>>;

    /** Date value with mode information */
    "date": DateWithMode;

    /** Setter for date */
    "setDate": Dispatch<SetStateAction<DateWithMode>>;

    /** Whether date has been manually edited by user */
    "isEditedDate": boolean;

    /** Setter for edited date flag */
    "setIsEditedDate": Dispatch<SetStateAction<boolean>>;

    /** Setter for showing the itinerary panel */
    "setShowItineraryPanel": Dispatch<SetStateAction<boolean>>;

    /**
     * Switches the itinerary panel to search-results mode.
     *
     * @remarks Must run in the same tick as showing the panel so a remount while
     * leftover mode was `"saved"` does not re-arm the one-shot OD search skip.
     */
    "setItineraryListContentMode": Dispatch<SetStateAction<ItineraryListContentMode>>;

    /** Departure selected address (for display in merge mode) */
    "departureSelectedAddress": SearchResultItem | null;

    /** Departure input value (for display in merge mode) */
    "departureInput": string;

    /** Setter for departure input */
    "setDepartureInput": Dispatch<SetStateAction<string>>;

    /** Reference to departure input element */
    "departureInputRef": React.RefObject<HTMLInputElement>;

    /** Setter for departure selected address */
    "setDepartureSelectedAddress": Dispatch<SetStateAction<SearchResultItem | null>>;

    /** Setter for departure autocomplete values */
    "setAutocompleteDepartureValues": Dispatch<SetStateAction<SearchResultItem[]>>;

    /** Setter for itineraries list */
    "setItineraries": Dispatch<SetStateAction<Route[]>>;

    /** Setter for selected itinerary ID */
    "setSelectedCarRouteId": Dispatch<SetStateAction<string>>;

    /** Setter for go (outbound) itineraries */
    "setGoItineraries": Dispatch<SetStateAction<Route[]>>;

    /** Setter for backup return itineraries */
    "setBackupReturnItineraries": Dispatch<SetStateAction<Route[]>>;
}

/**
 * Unified props type using discriminated union
 * 
 * @typedef {BaseSearchBarInputProps & (DepartureSpecificProps | ArrivalSpecificProps)} SearchBarInputProps
 */
type SearchBarInputProps = BaseSearchBarInputProps & (
    ( { "isArrival": false } & DepartureSpecificProps ) |
    ( { "isArrival": true } & ArrivalSpecificProps )
);

function searchFieldWrapperStyle( hasArrivalProps: boolean, topPosition: string ): React.CSSProperties {
    const transitionMs = SEARCH_BAR.topLayoutTransitionMs;
    if ( hasArrivalProps ) {
        return {
            "transitionDuration": `${ transitionMs }ms`,
            "top":                `${ topPosition }px`,
        };
    }

    return { "transitionDuration": `${ transitionMs }ms` };
}

/**
 * Unified Search Bar Input Component
 * 
 * Handles both departure and arrival location input with autocomplete functionality.
 * The component adapts its behavior based on the `isArrival` prop.
 * 
 * @param {SearchBarInputProps} props - Component props
 * @returns {JSX.Element} The rendered search bar input component
 * 
 * @component
 */
const SearchBarInput: React.FC<SearchBarInputProps> = ( props ) => {
    const {
        isArrival,
        autoComplete,
        setAutocompleteValues,
        lat,
        lon,
        input,
        setInput,
        isMerge,
        setIsMerge,
        inputRef,
        setSelectedItinerary,
        onResetPanelExpanded,
        setITakeMyCarData,
        setSelectedAddress,
        onSwap,
        myPositionAutocomplete,
        isDarkUi,
    } = props;
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );

    // Refs
    const ResultsRef = useRef<HTMLDivElement>( null );

    // Context hooks
    const [ searchHistory, setSearchHistory ] = usePersistedSlice( searchHistorySlice );

    const map = useMapInstance();
    const loadingItinerary = useEphemeralSelector( itineraryPanelStateSlice, ( panel ) => panel.loadingItinerary );

    // Extract arrival-specific props (TypeScript discriminated union ensures type safety)
    const arrivalProps = isArrival ? ( props as SearchBarInputProps & { "isArrival": true } & ArrivalSpecificProps ) : null;

    // Extract departure-specific props (TypeScript discriminated union ensures type safety)
    const departureProps = !isArrival ? ( props as SearchBarInputProps & { "isArrival": false } ) : null;

    // Helper to check if arrival props are available
    const hasArrivalProps = isArrival && arrivalProps != null;

    // Extract stable values from arrivalProps for useEffect dependencies
    const arrivalDateTimestamp = useMemo( () => {
        return arrivalProps?.date.timestamp ?? null;
    }, [ arrivalProps?.date.timestamp ] );

    const arrivalIsEditedDate = useMemo( () => {
        return arrivalProps?.isEditedDate ?? false;
    }, [ arrivalProps?.isEditedDate ] );

    const arrivalVisible = useMemo( () => {
        return arrivalProps?.visible ?? false;
    }, [ arrivalProps?.visible ] );

    const [ isModalOpen, setIsModalOpen ] = useState<boolean>( false );

    /**
     * Auto-update the arrival date on interval ticks only (not on tab resume),
     * matching the previous SearchBar minute-boundary behavior.
     */
    const handleArrivalIntervalTick = useCallback( (): void => {
        if ( !hasArrivalProps || arrivalProps === null ) {
            return;
        }

        if ( loadingItinerary || arrivalVisible ) {
            return;
        }

        const currentDate: DateWithMode = arrivalProps.date;
        arrivalProps.setDate( {
            "timestamp":            Date.now(),
            "isDepartureTimestamp": currentDate.isDepartureTimestamp,
        } );
    }, [
        arrivalProps,
        arrivalVisible,
        hasArrivalProps,
        loadingItinerary,
    ] );

    const shouldTickArrivalClock = hasArrivalProps &&
      arrivalDateTimestamp !== null &&
      !arrivalIsEditedDate;

    const { "isVisible": isPageVisible, "nowMs": currentTime } = usePageVisibility( {
        "trackNow":                true,
        "tickIntervalMs":          shouldTickArrivalClock ? SEARCH_BAR.refreshTimeIntervalMs : undefined,
        "alignToIntervalBoundary": shouldTickArrivalClock,
        "onIntervalTick":          shouldTickArrivalClock ? handleArrivalIntervalTick : undefined,
    } );

    // UI constants (simple calculations, no need for useMemo)
    const icon = !isArrival ? DepartureIcon : ArrivalIcon;
    const placeholder = !isArrival ? "Départ" : "Arrivée";

    /**
     * Reference point for geocode bias: map viewport center when the map exists,
     * otherwise user/device coordinates from props.
     */
    const getCoordinates = useCallback( () => {
        if ( map != null ) {
            return {
                "lat": map.getCenter().lat ?? 0,
                "lon": map.getCenter().lng ?? 0,
            };
        }

        return {
            lat,
            lon,
        };
    }, [ map, lat, lon ] );

    // Autocomplete hook
    const {
        showAutocomplete,
        setShowAutocomplete,
        showCompleteFromFallback,
        setShowCompleteFromFallback,
        getSuggestions,
        searchFallback,
        closeAutocomplete,
    } = useSearchBarAutocomplete( {
        setAutocompleteValues,
        searchHistory,
        getCoordinates,
        "resultsRef": ResultsRef,
    } );

    /**
     * When suggestions are open, lift the whole search block above the
     * floating menu layer (`z-40`) so the list paints over the FAB row
     * (vertical anchor still uses the pill row only in Home).
     */
    const zIndex = useMemo( () => {
        if ( showAutocomplete ) {
            return "z-[45]";
        }

        return isMerge
            ? !isArrival ? "z-20" : "z-30"
            : !isArrival ? "z-30" : "z-20";
    }, [ showAutocomplete, isMerge, isArrival ] );

    /**
     * In merge mode, keep autocomplete backdrop blur on the arrival pill and
     * suggestion list only so it does not bleed onto DepartureDisplay.
     */
    const shouldIsolateAutocompleteBlur = showAutocomplete && isMerge && isArrival;

    /**
     * Calculate whether to show refresh button
     * 
     * Shows refresh button when current time (rounded to minute) exceeds selected time (rounded to minute) by at least SEARCH_BAR.displayRefreshButtonMinMinutesDifference minutes
     */
    const shouldShowRefreshButton = useMemo( () => {
        if ( !hasArrivalProps || arrivalProps === null ) {
            return false;
        }

        const { "date": arrivalDate } = arrivalProps;

        if ( !isPageVisible ) {
            return false;
        }

        // Round timestamps to the nearest minute
        const MILLISECONDS_PER_MINUTE = 60000;
        const currentTimeRounded = Math.floor( currentTime / MILLISECONDS_PER_MINUTE ) * MILLISECONDS_PER_MINUTE;
        const selectedTimestampRounded = Math.floor( arrivalDate.timestamp / MILLISECONDS_PER_MINUTE ) * MILLISECONDS_PER_MINUTE;

        return currentTimeRounded - selectedTimestampRounded >= SEARCH_BAR.displayRefreshButtonMinMinutesDifference * MILLISECONDS_PER_MINUTE;
    }, [ hasArrivalProps, arrivalProps, isPageVisible, currentTime ] );

    /**
     * Handle merge mode for arrival input
     * 
     * Automatically sets departure to "Ma position" if empty when merge mode is activated.
     * This ensures a default departure location when user selects an arrival.
     * 
     * @effect
     */
    useEffect( () => {
        if ( !hasArrivalProps || !isMerge ) {
            return;
        }

        const {
            "departureInput": departureInput,
            "setDepartureInput": setDepartureInput,
            "setDepartureSelectedAddress": setDepartureSelectedAddress,
        } = arrivalProps;

        if ( departureInput === "" ) {
            setDepartureInput( myPositionAutocomplete.resume );
            setDepartureSelectedAddress( myPositionAutocomplete );
        }
    }, [ hasArrivalProps, isMerge, arrivalProps, myPositionAutocomplete ] );

    /**
     * Handle input value changes
     * 
     * @param {string} e - New input value
     * @function handleChanges
     */
    const handleChanges = useCallback(
        ( e: string ) => {
            setInput( e );
            getSuggestions( e );
        },
        [ getSuggestions, setInput ],
    );

    /**
     * Handle address selection from autocomplete
     * 
     * When an address is selected:
     * - Saves to search history (if not "Ma position")
     * - Updates selected address and input value
     * - Resets related states
     * - Enables merge mode if conditions are met
     * - For arrival: switches to search content mode, shows the itinerary panel,
     *   and resets itinerary states so OD search is not skipped after a saved-list remount
     * 
     * @param {SearchResultItem} item - Selected geocoding item
     * @function handleAddress
     */
    const handleAddress = useCallback(
        ( item: SearchResultItem ) => {
            // Save to search history if not "Ma position"
            if ( item.source !== GeocodeSource.MYLOCATION ) {
                setSearchHistory( ( previous ) => appendSearchHistoryItem( previous, item ) );
            }

            // Clear autocomplete and update state
            setAutocompleteValues( [] );
            setShowAutocomplete( false );
            setSelectedAddress( item );
            setInput( item.resume );
            setSelectedItinerary( null );
            setITakeMyCarData( null );
            onResetPanelExpanded();
            setShowCompleteFromFallback( true );

            if ( item.type === GeocodeType.PLACE ) {
                const itineraryService = new ItineraryService();
                itineraryService.reverseGeocode( item.location.coordinates.lat, item.location.coordinates.lon, GeocodeType.ADDRESS ).then( ( results ) => {
                    item.resume = item.result + " (" + results[0].title + ", " + results[0].subtitle + ")";
                    setSelectedAddress( item );
                    setInput( item.resume );
                } ).
                    catch( ( error ) => {
                        console.error( error );
                    } );
            }

            // Type-specific merge mode handling
            if ( !isArrival && departureProps != null && departureProps.arrivalInput.length > 0 ) {
                // If arrival input has value, enable merge mode
                setIsMerge( true );
            } else if ( hasArrivalProps ) {
                /*
                 * Default departure to "Ma position" in the same tick as showing the itinerary
                 * panel. A deferred fill (useEffect) races with Home's panel-hide effect while
                 * departureInput is still empty, which unmounts ItineraryList before the first search.
                 */
                if ( arrivalProps.departureInput === "" ) {
                    arrivalProps.setDepartureInput( myPositionAutocomplete.resume );
                    arrivalProps.setDepartureSelectedAddress( myPositionAutocomplete );
                }

                setIsMerge( true );

                // Leave saved-list mode before the panel remounts so OD search is not skipped.
                arrivalProps.setItineraryListContentMode( "search" );
                arrivalProps.setShowItineraryPanel( true );
                arrivalProps.setItineraries( [] );
                arrivalProps.setSelectedCarRouteId( "" );
                arrivalProps.setGoItineraries( [] );
                arrivalProps.setBackupReturnItineraries( [] );
            }
        },
        [
            isArrival,
            departureProps,
            hasArrivalProps,
            arrivalProps,
            myPositionAutocomplete,
            setSearchHistory,
            setAutocompleteValues,
            setShowAutocomplete,
            setSelectedAddress,
            setInput,
            setIsMerge,
            setSelectedItinerary,
            setITakeMyCarData,
            onResetPanelExpanded,
            setShowCompleteFromFallback,
        ],
    );


    /**
     * Reset all fields for input
     * 
     * Clears all autocomplete values, selected addresses, and resets component state.
     * 
     * @function resetAllFields
     */
    const resetAllFields = useCallback( () => {
        setInput( "" );
        closeAutocomplete();
        setSelectedItinerary( null );
        setSelectedAddress( null );
        onResetPanelExpanded();
        arrivalProps?.setVisible( false );
        setITakeMyCarData( null );
        setShowCompleteFromFallback( true );
    }, [
        arrivalProps,
        closeAutocomplete,
        setInput,
        setSelectedAddress,
        setSelectedItinerary,
        onResetPanelExpanded,
        setITakeMyCarData,
        setShowCompleteFromFallback,
    ] );

    /**
     * Handle icon click for departure input
     * 
     * Clears input and resets state when clicking the departure icon.
     * 
     * @function handleIconClick
     */
    const handleIconClick = useCallback( () => {
        if ( !isArrival ) {
            setInput( "" );
            closeAutocomplete();
            setSelectedAddress( null );
            setIsMerge( true );
        }
    }, [ closeAutocomplete, isArrival, setInput, setSelectedAddress, setIsMerge ] );

    /**
     * Handle input focus event
     *
     * Triggers suggestions when input is focused and selects all text.
     *
     * @function handleInputFocus
     */
    const handleInputFocus = useCallback( () => {
        handleChanges( input );

        if ( inputRef.current !== null ) {
            inputRef.current.select();
        }
    }, [ handleChanges, input, inputRef ] );


    return (
        <>
            {hasArrivalProps && arrivalProps !== null && (
                <CalendarSection
                    currentOrSelectedDate={arrivalProps.date.timestamp}
                    date={arrivalProps.date}
                    isDarkUi={isDarkUi}
                    isEditedDate={arrivalProps.isEditedDate}
                    isModalOpen={isModalOpen}
                    onRefreshDate={() => {
                        const now = Date.now();
                        const currentDate: DateWithMode = arrivalProps.date;
                        arrivalProps.setDate( {
                            "timestamp":            now,
                            "isDepartureTimestamp": currentDate.isDepartureTimestamp,
                        } );
                    }}
                    setDate={arrivalProps.setDate}
                    setIsEditedDate={arrivalProps.setIsEditedDate}
                    setIsModalOpen={setIsModalOpen}
                    shouldShowRefreshButton={shouldShowRefreshButton}
                />
            )}
            <div
                className={`absolute right-3 left-3 ml-auto mr-auto ${ zIndex } transition-top ease md:w-3/5 ${ !shouldIsolateAutocompleteBlur && !isMerge ? "backdrop-blur-xs" : "" } ${ !isArrival ? "top-12" : "" }`}
                id={isArrival ? "SearchInputArrival" : "SearchInputDeparture"}
                style={searchFieldWrapperStyle( hasArrivalProps, hasArrivalProps ? arrivalProps.position : "" )}
            >
                {hasArrivalProps && isMerge && (
                    <DepartureDisplay
                        departureInput={arrivalProps.departureInput}
                        departureInputRef={arrivalProps.departureInputRef}
                        setIsMerge={setIsMerge}
                    />
                )}
                <div className={shouldIsolateAutocompleteBlur ? "backdrop-blur-xs" : ""}>
                <div
                    className={`flex items-center shadow-md h-12 px-3 py-2 ${ !isArrival ? "rounded-tr-none" : "" } ${ isMerge ? showAutocomplete ? "rounded-t-none" : "rounded-b-xl" : "rounded-xl" } ${ isMerge && !isArrival ? "hidden" : "" } ${ showAutocomplete ? isMerge && isArrival ? "" : "rounded-b-none rounded-t-xl" : "" } relative`}
                    id={isArrival ? "SearchInputArrivalRow" : "SearchInputDepartureRow"}
                    style={{ "backgroundColor": palette.overlayCardBackgroundStrong }}
                >
                    {( !hasArrivalProps || !( onSwap != null && !showAutocomplete && ( ( !isMerge && !hasArrivalProps ) || ( isMerge && hasArrivalProps ) ) ) ) && (
                        <div
                            className="cursor-pointer w-[32px] items-center justify-center flex"
                            onClick={handleIconClick}
                        >
                            <img
                                alt={placeholder}
                                className="w-[55%]"
                                src={icon}
                            />
                        </div>
                    )}
                    {onSwap != null && !showAutocomplete && ( ( !isMerge && !hasArrivalProps ) || ( isMerge && hasArrivalProps ) ) && (
                        <button
                            className={`z-50 border-2 border-solid backdrop-blur-sm rounded-full flex items-center justify-center cursor-pointer transition-colors ${
                                isMerge && hasArrivalProps
                                    ? "right-[50px] top-[44px] w-8 h-8"
                                    : "absolute left-[50%] bottom-[-25px] w-10 h-10"
                            }`}
                            onClick={onSwap}
                            style={{
                                "borderColor":     palette.brandPrimary,
                                "backgroundColor": palette.overlayCardBackgroundSoft,
                            }}
                            type="button"
                        >
                            <svg
                                aria-label="Swap departure and arrival"
                                className={`${ isMerge && hasArrivalProps ? "w-5 h-5" : "w-6 h-6" }`}
                                fill="none"
                                style={{ "color": palette.brandPrimary }}
                                transform="rotate(90)"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g
                                    id="SVGRepo_bgCarrier"
                                    strokeWidth="0"
                                />
                                <g
                                    id="SVGRepo_tracerCarrier"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <g id="SVGRepo_iconCarrier">
                                <path
                                    d="M21 7.5L8 7.5M21 7.5L16.6667 3M21 7.5L16.6667 12"
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                />
                                <path
                                    d="M4 16.5L17 16.5M4 16.5L8.33333 21M4 16.5L8.33333 12"
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                />
                                </g>
                            </svg>
                        </button>
                    )}
                    <div
                        className="w-full px-3 grid pr-0.5"
                    >
                        <div className="flex">
                            <div className="w-full flex items-center justify-center">
                                <input
                                    autoCapitalize="off"
                                    autoComplete="off"
                                    autoCorrect="off"
                                    className="w-full border-none outline-none bg-transparent font-medium m-0"
                                    onChange={( e ) => handleChanges( e.target.value )}
                                    onFocus={handleInputFocus}
                                    placeholder={placeholder}
                                    ref={inputRef}
                                    spellCheck={false}
                                    style={{ "color": palette.overlayTextPrimary }}
                                    type="text"
                                    value={input}
                                />
                            </div>
                            { isArrival && loadingItinerary && (
                                <span className="h-1">
                                    <IonSpinner
                                        className="w-5 mt-[-2px]"
                                        color="primary"
                                    />
                                </span>
                            )}
                            {( ( showAutocomplete || input.length > 0 ) && !loadingItinerary ) && (
                                <span
                                    className="p-1 h-full flex items-center justify-center cursor-pointer"
                                    onClick={resetAllFields}
                                >
                                    <img
                                        alt="Clear"
                                        className="w-[16px]"
                                        src={DarkCloseIcon}
                                    />
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                {/* Auto complete response */}
                <AnimatePresence>
                    {showAutocomplete
                        ? (
                            <AutocompleteList
                                autoComplete={autoComplete}
                                input={input}
                                isDarkUi={isDarkUi}
                                myPositionAutocomplete={myPositionAutocomplete}
                                onAddressSelect={handleAddress}
                                onFallbackSearch={searchFallback}
                                onOutsideClick={closeAutocomplete}
                                resultsRef={ResultsRef}
                                showCompleteFromFallback={showCompleteFromFallback}
                            />
                        )
                        : null}
                </AnimatePresence>
                </div>
            </div>
        </>
    );
};

export default SearchBarInput;
